/**
 * Custom hooks extracted from `PostStreamReviewScreen` to keep the screen
 * component readable and to isolate the subtler effect-timing logic for unit
 * testing.
 */
import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { openPostStreamReviewModal } from '../../../../../editor-actions/actions';
import type { AIContentPositionEntry } from '../../../../../pm-plugins/ai-session-context/types';

/**
 * Close the modal when the user clicks inside the editor but outside the toolbar.
 *
 * We listen on `pointerdown` (rather than `click`) so the dismissal fires
 * before the editor's own selection handler runs — clicking into the editor
 * body to keep editing should still close the toolbar cleanly without the
 * toolbar momentarily competing for the same selection.
 *
 * The listener is attached at the document level in the capture phase so
 * descendant `stopPropagation()` calls (e.g. from popups rendered inside the
 * prompt input) don't accidentally suppress the outside-click detection. We
 * then explicitly skip events whose target is contained by the toolbar root
 * (`screenRef`) and events inside any element carrying the
 * `data-post-stream-review-portal` escape-hatch attribute, so portalled
 * descendants (Popup, Tooltip, Menu) that render to `document.body` are
 * treated as "inside" the toolbar.
 *
 * Dismissal is further scoped to clicks that land inside the editor itself
 * (`editorView.dom`): only an outside-the-toolbar click within the editor
 * accepts and closes the review. A click on unrelated page chrome outside the
 * editor (side panels, other surfaces) leaves the review open, so the user
 * doesn't lose an in-progress review by interacting with the rest of the page.
 */
export function useOutsideClickClose(
	screenRef: React.RefObject<HTMLElement | null>,
	editorView: EditorView | undefined,
	close: () => void,
): void {
	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}
			if (screenRef.current?.contains(target)) {
				return;
			}
			if (target instanceof Element && target.closest('[data-post-stream-review-portal]')) {
				return;
			}
			// Only dismiss when the click lands inside the editor itself. A click
			// outside the editor (page chrome, other surfaces) must not close an
			// in-progress review.
			const editorDom = editorView?.dom;
			if (!editorDom || !editorDom.contains(target)) {
				return;
			}
			close();
		};
		const unbind = bind(doc, {
			type: 'pointerdown',
			listener: handlePointerDown,
			options: { capture: true },
		});
		return unbind;
	}, [screenRef, editorView, close]);
}

/**
 * Options for `useReviewKeyboardShortcuts`. All values are read live (via a
 * ref) on each keystroke, so the listener never needs re-binding when they change.
 */
export interface ReviewKeyboardShortcutOptions {
	/** Next-change navigation is available (`> 1` reviewable change). */
	canGoNext: boolean;
	/** Previous-change navigation is available (`> 1` reviewable change). */
	canGoPrevious: boolean;
	/** Redo is available (mirrors the Redo button's enabled state). */
	canRedo: boolean;
	/** Undo is available (mirrors the Undo button's enabled state). */
	canUndo: boolean;
	/**
	 * Whether the review has been accepted and the "Accepted" pill has replaced the
	 * toolbar. While true every shortcut stands down (the review is tearing down).
	 */
	isAccepted: boolean;
	/**
	 * Whether the re-prompt input is empty. A bare Enter accepts only when empty;
	 * with typed text it is left for the re-prompt submit. Undo/redo likewise
	 * defer to the browser's native text undo/redo while the prompt has content.
	 */
	isPromptEmpty: boolean;
	/** Whether step-through mode is expanded — navigation is only active then. */
	isStepThroughExpanded: boolean;
	/** Accept the current change — bare Enter while the prompt is empty. */
	onAccept: () => void;
	onNext: () => void;
	onPrevious: () => void;
	onRedo: () => void;
	onUndo: () => void;
}

/**
 * Keyboard shortcuts for the Review moment toolbar [CCI-18059]. A single
 * `document` keydown listener in the capture phase that reacts only to events
 * fired inside the toolbar (`screenRef`, plus the
 * `data-post-stream-review-portal` escape-hatch for portalled descendants) —
 * during a review the prompt is focused, so keystrokes land inside the toolbar;
 * clicking into the editor body dismisses the review (see `useOutsideClickClose`).
 *
 * Bindings (the undo/redo and navigation ones MUST stay in sync with the tooltip
 * hints rendered in `AskRovo.tsx` and `ActionButtons.tsx`):
 *   - Accept: Enter            (bare — only while the prompt is empty)
 *   - Undo:   Mod-z            (⌘Z / Ctrl+Z)
 *   - Redo:   Mod-Shift-z, or Ctrl-y (⌘⇧Z / Ctrl+Y — Windows/Linux alias)
 *   - Prev:   Alt-ArrowUp      (⌥↑ / Alt+↑) — step-through only
 *   - Next:   Alt-ArrowDown    (⌥↓ / Alt+↓) — step-through only
 *
 * The handlers are the same callbacks the toolbar buttons invoke, so behaviour
 * (accept-and-advance, single-transaction undo/redo, `hideDiff()` first,
 * wrap-around navigation) is identical to clicking. `preventDefault()` is only
 * called when a shortcut is actually actioned, so unrelated / disabled keystrokes
 * pass through untouched — in particular, while the prompt has text a bare Enter
 * and ⌘Z/⌘⇧Z fall through to the re-prompt submit / native text undo/redo.
 */
export function useReviewKeyboardShortcuts(
	screenRef: React.RefObject<HTMLElement | null>,
	options: ReviewKeyboardShortcutOptions,
): void {
	const optionsRef = useRef(options);
	optionsRef.current = options;
	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const handleKeyDown = (event: KeyboardEvent) => {
			// Let IME composition through untouched.
			if (event.isComposing) {
				return;
			}
			const target = event.target;
			// Keydown targets we care about are always Elements; narrowing to Element
			// (a Node subtype) makes `contains`/`closest` type-safe below.
			if (!(target instanceof Element)) {
				return;
			}
			// Only react to keystrokes within the toolbar (incl. portalled descendants).
			const insideToolbar =
				screenRef.current?.contains(target) || !!target.closest('[data-post-stream-review-portal]');
			if (!insideToolbar) {
				return;
			}

			const opts = optionsRef.current;

			// Once accepted, the toolbar is replaced by the "Accepted" pill and the
			// review is tearing down — every shortcut stands down (this also preserves
			// the old Enter-accept gate, which was `!hasInputText && !isAccepted`).
			if (opts.isAccepted) {
				return;
			}

			// Accept — bare Enter (no modifiers), only while the prompt is empty. With
			// typed text the Enter is left for the re-prompt submit.
			if (event.key === 'Enter') {
				if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
					return;
				}
				if (!opts.isPromptEmpty) {
					return;
				}
				event.preventDefault();
				opts.onAccept();
				return;
			}

			// Navigation — Alt+ArrowUp / Alt+ArrowDown, only while stepping through.
			// Requires Alt alone (no Cmd/Ctrl/Shift) so it can't collide with an
			// editor/OS Alt+Shift+Arrow selection or Cmd+Alt combo.
			if (
				event.altKey &&
				!event.metaKey &&
				!event.ctrlKey &&
				!event.shiftKey &&
				(event.key === 'ArrowUp' || event.key === 'ArrowDown')
			) {
				if (!opts.isStepThroughExpanded) {
					return;
				}
				if (event.key === 'ArrowUp') {
					if (!opts.canGoPrevious) {
						return;
					}
					event.preventDefault();
					opts.onPrevious();
				} else {
					if (!opts.canGoNext) {
						return;
					}
					event.preventDefault();
					opts.onNext();
				}
				return;
			}

			// Undo/redo — require the primary modifier (⌘ on macOS, Ctrl elsewhere).
			const hasPrimaryModifier = event.metaKey || event.ctrlKey;
			if (!hasPrimaryModifier) {
				return;
			}
			// Defer to native text undo/redo while the re-prompt has typed content.
			if (!opts.isPromptEmpty) {
				return;
			}
			const key = event.key.toLowerCase();
			if (key === 'z' && !event.altKey) {
				if (event.shiftKey) {
					// Redo — Mod-Shift-z.
					if (!opts.canRedo) {
						return;
					}
					event.preventDefault();
					opts.onRedo();
				} else {
					// Undo — Mod-z.
					if (!opts.canUndo) {
						return;
					}
					event.preventDefault();
					opts.onUndo();
				}
				return;
			}
			// Redo — Ctrl-y (Windows/Linux alias). Cmd+Y is NOT redo on macOS, so
			// require Ctrl specifically and exclude the Cmd modifier.
			if (key === 'y' && event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
				if (!opts.canRedo) {
					return;
				}
				event.preventDefault();
				opts.onRedo();
			}
		};
		const unbind = bind(doc, {
			type: 'keydown',
			listener: handleKeyDown,
			options: { capture: true },
		});
		return unbind;
	}, [screenRef]);
}

/**
 * Auto-hide the diff overlay when the screen unmounts (modal closed for any
 * reason — `endExperience`, slice replace, full session reset). Without this
 * the diff decorations would linger on the page after the toolbar goes away.
 *
 * IMPORTANT: this effect MUST only depend on the empty array so the cleanup
 * runs exclusively at unmount. An earlier version listed `coreActions` and
 * `hideDiff` as deps, but those reference identities can change across
 * renders — and `coreActions.execute(showDiff(...))` itself triggers a
 * re-render of the parent component via the shared plugin-state subscription,
 * which invalidated the deps and ran the cleanup mid-render, immediately
 * hiding the diff the user had just asked to see (the View Changes button
 * effectively did nothing visible).
 *
 * To keep the cleanup correct (it must call the *latest* `hideDiff` command
 * at unmount time) we capture the action + command into a ref that we keep
 * up to date on every render, and have the unmount cleanup read from the
 * ref. This decouples cleanup-timing from dep-identity changes.
 */
export function useHideDiffOnUnmount(
	coreActions: { execute: (cmd: never) => unknown } | undefined,
	hideDiff: unknown | undefined,
): void {
	const hideOnUnmountRef = useRef<(() => void) | null>(null);
	hideOnUnmountRef.current = () => {
		if (coreActions && hideDiff) {
			coreActions.execute(hideDiff as never);
		}
	};
	useEffect(() => {
		return () => {
			hideOnUnmountRef.current?.();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}

/**
 * Segment navigation: Prev/Next re-anchor the modal at the target segment by
 * dispatching a fresh `openPostStreamReviewModal` (next microtask) with that
 * segment's `[startPos, endPos]`. The decoration plugin swaps the anchor
 * decoration and ModalRegion re-renders — no experience teardown needed.
 *
 * Navigation wraps around and needs the actual `segments` (not just a count) so
 * each hop can anchor at the right positions.
 *
 * `onScrollToSegment` (optional) brings the navigated segment's `[startPos, endPos]`
 * range into view — it is driven from here (with the exact target index in hand)
 * rather than a reactive effect, so it never fires on a stale intermediate render
 * during the toggle.
 *
 * NOTE: do NOT call `endExperience(...)` here — it is the close-button prop and
 * fully closes the modal, so the racing reopen loses and Prev/Next reads as "close".
 */
export function useSegmentNavigation({
	editorView,
	segmentIndex,
	segments,
	onScrollToSegment,
}: {
	editorView: EditorView | undefined;
	onScrollToSegment?: (startPos: number, endPos: number) => void;
	segmentIndex: number;
	segments?: AIContentPositionEntry[];
}): {
	canGoNext: boolean;
	canGoPrevious: boolean;
	goToNext: () => void;
	goToPrevious: () => void;
} {
	const goToSegment = useCallback(
		(newIndex: number) => {
			if (!editorView || !segments || !segments[newIndex]) {
				return;
			}
			Promise.resolve().then(() => {
				if (editorView && !editorView.isDestroyed) {
					openPostStreamReviewModal({
						editorView,
						segmentData: {
							segmentIndex: newIndex,
							positions: {
								startPos: segments[newIndex].startPos,
								endPos: segments[newIndex].endPos,
							},
						},
					});
					onScrollToSegment?.(segments[newIndex].startPos, segments[newIndex].endPos);
				}
			});
		},
		[editorView, segments, onScrollToSegment],
	);

	// Arrows enabled only with more than one segment; navigation wraps around.
	const totalSegments = segments?.length ?? 0;
	const canGoPrevious = totalSegments > 1;
	const canGoNext = totalSegments > 1;

	const goToPrevious = useCallback(() => {
		if (totalSegments <= 1) {
			return;
		}
		// Wrap: from 0 -> last.
		const newIndex = (segmentIndex - 1 + totalSegments) % totalSegments;
		goToSegment(newIndex);
	}, [segmentIndex, totalSegments, goToSegment]);

	const goToNext = useCallback(() => {
		if (totalSegments <= 1) {
			return;
		}
		// Wrap: from last -> 0.
		const newIndex = (segmentIndex + 1) % totalSegments;
		goToSegment(newIndex);
	}, [segmentIndex, totalSegments, goToSegment]);

	return { canGoNext, canGoPrevious, goToNext, goToPrevious };
}
